import React from 'react'
import classNames from 'classnames'

import { CloseButton } from '../../Dialog'
import type { UseLinkPreview } from './useLinkPreview'

import styles from './styles.module.scss'

type Props = {
  preview: UseLinkPreview
}

/**
 * The composer's link-preview affordance. One card-shaped container carries
 * every state — idle skeleton, loading shimmer, error notice — so idle →
 * loading → ready reads as the same element filling in, not a widget swap;
 * `ready` then shows the baked card itself in the same slot.
 */
export default function LinkPreviewGhost({ preview }: Props) {
  const { phase } = preview
  if (!phase) {
    return null
  }

  // The aria-live wrapper is this outer div, which stays mounted across every
  // phase change — a live region that appears together with its content (e.g.
  // aria-live on the per-state divs) is not reliably announced, and the
  // loading → ready swap would be silent for screen-reader users.
  return (
    <div aria-live='polite'>
      <LinkPreviewPhaseView preview={preview} phase={phase} />
    </div>
  )
}

function LinkPreviewPhaseView({
  preview,
  phase,
}: Props & { phase: NonNullable<UseLinkPreview['phase']> }) {
  if (phase.kind === 'ready') {
    // The baked card IS the preview (title/host/image are baked into it), so
    // show it big and skip the redundant text; keep only the size toggle (with
    // a word, not just a glyph) and remove.
    const isHero = phase.layout === 'hero'
    return (
      <div className={styles.ready} data-testid='link-preview-ready'>
        <img
          className={styles.preview}
          src={phase.image}
          alt='Link preview to be sent'
        />
        <div className={styles.readyControls}>
          <button
            type='button'
            className={styles.labelButton}
            onClick={preview.onToggleLayout}
            data-testid='link-preview-toggle'
            aria-label={isHero ? 'Collapse to compact' : 'Expand to hero'}
          >
            {isHero ? '⤡ Smaller' : '⤢ Bigger'}
          </button>
          <CloseButton
            onClick={preview.onRemove}
            aria-label='Remove preview'
            data-testid='link-preview-remove'
          />
        </div>
      </div>
    )
  }

  // idle / loading / unavailable: the same skeleton of the compact card "Add"
  // will bake — light surface, placeholder title lines + the real host, and a
  // thumbnail box on the right. Loading only starts the shimmer; unavailable
  // only dims the card — the silhouette never changes, so the states morph
  // into each other instead of replacing one another.
  const { kind } = phase
  const testId =
    kind === 'idle'
      ? 'link-preview-ghost'
      : kind === 'loading'
        ? 'link-preview-loading'
        : 'link-preview-unavailable'
  const hint =
    kind === 'idle'
      ? 'Click “Add” to fetch and attach a preview'
      : kind === 'loading'
        ? 'Generating preview…'
        : 'Link previews need a bridge with unfurl enabled.'
  return (
    <div className={styles.ghost} data-testid={testId}>
      <div className={styles.ghostLeft}>
        <div
          className={classNames(styles.ghostCard, {
            [styles.ghostCardLoading]: kind === 'loading',
            [styles.ghostCardMuted]: kind === 'unavailable',
          })}
          aria-hidden='true'
        >
          <div className={styles.ghostBody}>
            <span className={styles.skelLine} />
            <span className={`${styles.skelLine} ${styles.skelLineShort}`} />
            <span className={styles.ghostHost}>{phase.host}</span>
          </div>
          <span className={styles.ghostThumb} />
        </div>
        <span className={styles.ghostHint}>{hint}</span>
      </div>
      <div className={styles.ghostActions}>
        {kind === 'idle' && (
          <button
            type='button'
            className={styles.addButton}
            onClick={preview.onAdd}
            data-testid='link-preview-add'
            aria-label='Add link preview'
          >
            Add
          </button>
        )}
        {kind !== 'loading' && (
          <CloseButton
            onClick={preview.onDismiss}
            aria-label='Dismiss preview'
            data-testid='link-preview-dismiss'
          />
        )}
      </div>
    </div>
  )
}
